import React, {Component} from "react";
import {loadScript} from "../../utils/browser";
import {UserContext} from "../../context/UserContext";
import {Loading} from "../../components/Loading";
import {AdobeAnalyticsContextData} from "../../context/AdobeAnalyticsContext";
import {ProfileEntity} from "../../data/entities/Connect/ProfileEntity";

interface State {
    loading: boolean;
}

interface Props {
    applicationId: string | undefined;
    environment: string;
    genomeId: string | undefined;
    locale: string;
    analytics: AdobeAnalyticsContextData;
}

interface UbisoftConnectSDK {
    readonly init: (options: object) => void;
    readonly reInit: () => void;
    readonly getTicket: (callback: (response: UbisoftConnectResponse) => void) => void;
    readonly getProfiles: (callback: (response: UbisoftGetProfilesResponse) => void) => void;
    readonly changeLocale: (locale: string) => void;
}

export interface UbisoftConnectResponse {
    readonly status: 'ok' | 'error';
    readonly payload: {
        readonly userId: string;
        readonly expiration: string;
        readonly sessionId: string;
        readonly ticket: string;
    };
}

export interface UbisoftGetProfilesResponse {
    readonly status: 'ok' | 'error';
    readonly payload: {
        profiles: ProfileEntity[];
    };
}

export default class OverlayAuth extends Component<Props, State> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;
    SDK!: UbisoftConnectSDK;

    refreshTimer: ReturnType<typeof setInterval> | null = null;

    private resourceUrls: object = {
        uat: "https://uat-overlay.ubisoft.com/overlay-connect-integration/widget.js",
        preprod: "https://overlay.ubisoft.com/overlay-connect-integration/widget.js",
        prod: "https://overlay.ubisoft.com/overlay-connect-integration/widget.js",
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            loading: true,
        };
    }

    componentDidMount(): void {
        loadScript(this.resourceUrls[this.props.environment]).then(this.init);
        window.addEventListener('message', this.listenWindowMessages, false);
    }

    componentWillUnmount() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }

        window.removeEventListener('message', this.listenWindowMessages, false);
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (prevProps.locale !== this.props.locale) {
            this.SDK.changeLocale(this.props.locale);
        }
    }

    listenWindowMessages = (event: MessageEvent) => {
        if (event.data === "EXTERNAL_UBISOFT_CONNECT") {
            this.setState({loading: true});
            this.SDK.reInit();
        }
    };

    init = () => {
        const {applicationId, genomeId, locale} = this.props;

        this.SDK = (window as any).overlayWidget;

        const options: object = {
            appId: applicationId,
            genomeId: genomeId,
            container: "#overlay-widget",
            locale: locale,
            onConnect: this.handleConnect,
            darkBackground: true,
            themeColor: "#101216",
            zIndex: "10",
            height: 60,
            loginType: "popup"
        };

        this.SDK.init(options);
        this.refreshTimer = setInterval(this.refreshTicket, 3600000);
    };

    refreshTicket = (): void => {
        this.SDK.getTicket((response): void => {
            this.handleConnect('refresh', response);
        });
    };

    render(): JSX.Element {
        if (this.state.loading) {
            return <Loading/>;
        } else {
            return this.props.children as JSX.Element;
        }
    }

    handleConnect = (action: string, response: UbisoftConnectResponse) => {
        switch (action) {
            case 'login':
            case 'init':
            case 'reInit':
            case 'refresh':
                this.handleConnectResponse(response);
                break;

            case 'logout':
                this.handleLogout();
                break;
        }
    };

    handleLogout = () => {
        this.props.analytics.track("accountevent", {
            ubisoftId: this.context.user.ticketData.userId,
            loggedIn: "false",
            loggedOut: "true",
            action: "page - login - logged out"
        });
        this.context.logout(() => {
            this.setState({loading: false})
        });
    };

    handleConnectResponse = (response: UbisoftConnectResponse) => {
        if (response.status === 'ok') {
            this.handleSuccessfulLogin(response);
        } else {
            this.handleLogout();
        }
    };

    private handleSuccessfulLogin(loginResponse: UbisoftConnectResponse) {
        this.SDK.getProfiles((profilesResponse: UbisoftGetProfilesResponse) => {
            if (profilesResponse.status === "ok") {
                this.context.login(loginResponse.payload, profilesResponse.payload.profiles, () => {
                    this.setState({loading: false})
                });
            } else {
                window.location.reload();
            }
        });
    }
}